import type { MeetingRecording } from '@/lib/api/meeting-recordings.api'
import { useState } from 'react'
import {
  canRetryRecording,
  formatRecordingDuration,
  recordingFailureMessage,
  RecordingStatusChip,
} from '@/components/meetings/RecordingStatusChip'
import { Button } from '@/components/ui/button'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { cn } from '@/lib/utils'
import { formatRelativeTime } from '@/utils/date'
import {
  ArrowClockwiseIcon,
  ArrowSquareOutIcon,
  SparkleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'

/**
 * The user's most recent recording and where its notes are in the pipeline,
 * shown in the idle record popover so "did my notes come through?" doesn't
 * need a trip to the Recordings tab. Finished notes the user hasn't opened yet
 * lead with a primary "Review notes" action.
 */
export function LastRecordingRow({
  recording,
  isReadyToReview,
  onOpenNote,
  onRetry,
  onNavigate,
  onDismiss,
  className,
}: {
  recording: MeetingRecording | undefined
  isReadyToReview: boolean
  onOpenNote: () => void
  onRetry: (id: string) => Promise<unknown>
  onNavigate?: () => void
  onDismiss?: () => void
  className?: string
}) {
  const [isRetrying, setIsRetrying] = useState(false)
  if (!recording) return null

  const handleRetry = async () => {
    setIsRetrying(true)
    try {
      await onRetry(recording.id)
      toast.success('Processing restarted')
    } catch (error) {
      toast.error(
        getMutationErrorMessage(error, "Couldn't retry processing."),
        {
          description:
            error instanceof Error && error.message ? error.message : undefined,
        },
      )
    } finally {
      setIsRetrying(false)
    }
  }

  const isReady = recording.status === 'READY' && Boolean(recording.noteId)
  const isFailed = recording.status === 'FAILED'
  const when = formatRelativeTime(recording.recordedAt ?? recording.createdAt)

  return (
    <div className={cn('px-4 py-3', className)} data-last-recording>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          {isReadyToReview && (
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          )}
          {isReadyToReview ? 'Notes ready to review' : 'Last recording'}
        </span>
        {onDismiss && (
          <button
            type="button"
            className="rounded-cell hover:bg-surface -mr-1.5 -mt-1 flex h-6 w-6 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Dismiss last recording"
            title="Dismiss"
            onClick={onDismiss}
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {recording.title ?? 'Untitled meeting'}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {when}
            {recording.durationSeconds
              ? ` · ${formatRecordingDuration(recording.durationSeconds)}`
              : ''}
          </p>
        </div>
        {!isReadyToReview && <RecordingStatusChip recording={recording} />}
      </div>
      {isFailed && (
        <p
          className="mt-1.5 text-xs text-destructive"
          title={recording.errorMessage ?? undefined}
        >
          {recordingFailureMessage(recording.errorStage)}
        </p>
      )}
      {(isReady || canRetryRecording(recording)) && (
        <div className="mt-2">
          {isReady ? (
            <Button
              size="sm"
              // Unread notes take the primary (ink) action on the tinted card.
              variant={isReadyToReview ? 'default' : 'outline'}
              className={cn(
                'h-8 w-full gap-1.5 text-xs',
                !isReadyToReview && 'bg-surface',
              )}
              onClick={() => {
                onNavigate?.()
                onOpenNote()
              }}
            >
              {isReadyToReview ? (
                <SparkleIcon className="h-3.5 w-3.5" />
              ) : (
                <ArrowSquareOutIcon className="h-3.5 w-3.5" />
              )}
              {isReadyToReview ? 'Review notes' : 'Open note'}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="bg-surface h-8 w-full gap-1.5 text-xs"
              disabled={isRetrying}
              onClick={() => void handleRetry()}
            >
              <ArrowClockwiseIcon className="h-3.5 w-3.5" />
              Retry
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
